import { DirectionalDrillingState } from '@go-widgets/well-logs-widget';
import { observable, makeObservable, action, reaction, comparer } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import { requireService } from 'src/packages/di';
import { getNewAndDeletedFields } from 'src/packages/shared/utils/getNewAndDeletedElements';

import type { DirectionalDrillingWidgetEntity } from './DirectionalDrillingWidget.entity';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import type { WellListPanelEntity } from '../well-list-panel/WellListPanel.entity';
import type { DirectionalDrillingInitParams, DirectionalDrillingStateParams } from '@go-widgets/well-logs-widget';
import type { IReactionDisposer, ObservableMap } from 'mobx';
import type { TCreateWidgetOptions, TGroup } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import { getCorrectLocale } from '../operational-parameters-widget/OperationalParametersWidget.utils';
import { WellListWidgetEntity } from '../well-list-widget/WellListWidget.entity';

export class DirectionalDrillingWidgetStore implements IWidgetStore {
  private readonly directionalDrillingEntity: DirectionalDrillingWidgetEntity;
  groupSelectStore: GroupSelectStore | null = null;
  private readonly initParams: DirectionalDrillingInitParams;

  @observable isLoading: boolean = false;
  @observable isReady: boolean = false;
  @observable hasError: boolean = false;
  @observable disposers: ObservableMap<WidgetEntity, IReactionDisposer> = observable.map();

  onCreateDirDrillingWidget: (
    wellId: number | null,
    stateParams: Partial<DirectionalDrillingStateParams> | null,
    options?: TCreateWidgetOptions
  ) => void;
  @observable isGroupSelectOpen: boolean = false;

  constructor(
    directionalDrillingEntity: DirectionalDrillingWidgetEntity,
    groupSelectStore: GroupSelectStore,
    onCreateDirDrillingWidget: (
      wellId: number | null,
      stateParams: Partial<DirectionalDrillingStateParams> | null,
      options?: TCreateWidgetOptions
    ) => void,
    private readonly navigate: (wellId: number | null, wellboreId?: number | null) => void,
    private readonly wellListPanel: WellListPanelEntity,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly notifications = requireService('notifications'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference')
  ) {
    this.onCreateDirDrillingWidget = onCreateDirDrillingWidget;
    this.directionalDrillingEntity = directionalDrillingEntity;
    this.isGroupSelectOpen = directionalDrillingEntity.isGroupsDefaultOpened;

    if (groupSelectStore) {
      this.groupSelectStore = groupSelectStore;
    }

    this.initParams = {
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      axiosInstance: wellLogsWidgetAgent,
    };
    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  exitFullScreen() {
    this.directionalDrillingEntity.setFullscreen(false);
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.directionalDrillingEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.directionalDrillingEntity.setWellId(well);
  }

  @action.bound
  setGroupSelectStore(groupSelectStore: GroupSelectStore) {
    this.groupSelectStore = groupSelectStore;
  }

  @action.bound
  redirectToBasicParameters() {
    const wellId = this.directionalDrillingEntity.wellId;
    const wellboreId = this.directionalDrillingEntity.stateParams.wellboreId;
    this.navigate(wellId, wellboreId);
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.directionalDrillingEntity.setInActiveTab(value);
  }

  private mapDeletedWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      if (this.disposers.has(widget)) {
        const disposer = this.disposers.get(widget);

        disposer?.();

        this.disposers.delete(widget);
      }
    }
  }

  private mapNewWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      if (!(widget instanceof WellListWidgetEntity) || this.disposers.has(widget)) {
        continue;
      }

      const disposeWellListWidget = reaction(
        () => widget.selectedWellID,
        (wellId) => {
          this.directionalDrillingEntity.setWellId(wellId);
        }
      );

      this.disposers.set(widget, disposeWellListWidget);
    }
  }

  private getNewState(wellId: number): DirectionalDrillingState {
    return new DirectionalDrillingState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 520,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        active: this.directionalDrillingEntity.inActiveTab,
      },
      this.directionalDrillingEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInitializationFailed: (_, error) => {
          this.setReady(false);

          console.error(error);
          this.notifications.showErrorMessageT('errors:failedToInitWidget');
        },
        onInternalStateChanged: (state) => {
          this.directionalDrillingEntity.setStateParams(state);
        },
        onWellboreChanged: (wellboreName) => {
          this.directionalDrillingEntity.setWellboreName(wellboreName);
        },
        onRedirectToBasicParameters: () => {
          this.redirectToBasicParameters();
        },
      }
    );
  }

  initWidgetByWellId(wellId: number) {
    const newState = this.getNewState(wellId);
    this.directionalDrillingEntity.setState(newState);
  }

  effect = () => {
    this.setInActiveTab(true);
    const disposeGroupId = reaction(
      () => this.directionalDrillingEntity.groupId,
      (groupId) => {
        if (groupId === null || this.groupSelectStore == null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.directionalDrillingEntity, this.wellListPanel);
      }
    );

    const dispose = reaction(
      () => {
        if (this.groupSelectStore) {
          return this.groupSelectStore.groupManager.getWidgetsByGroupId(this.directionalDrillingEntity.groupId).slice();
        }
        return [];
      },
      (widgets, prevState) => {
        const prevWidgets = prevState ?? [];

        const { newElements: newWidgets, deletedElements: deletedWidgets } = getNewAndDeletedFields<WidgetEntity>(
          widgets,
          prevWidgets
        );

        this.mapNewWidgets(newWidgets);
        this.mapDeletedWidgets(deletedWidgets);
      },
      { fireImmediately: true, equals: comparer.shallow }
    );

    const disposeWellId = reaction(
      () => this.directionalDrillingEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.directionalDrillingEntity.state) {
          const newState = this.getNewState(wellId);

          this.directionalDrillingEntity.setState(newState);
        } else if (wellId && this.directionalDrillingEntity.state && typeof prevWellId !== 'undefined') {
          this.directionalDrillingEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.directionalDrillingEntity.state) {
          this.directionalDrillingEntity.state.destroy();
          this.directionalDrillingEntity.setState(null);
        }
      },
      { fireImmediately: true }
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForTime,
      }),
      ({ theme, language, isAltNames, defaultSelectWellbore }) => {
        this.directionalDrillingEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
          defaultSelectWellbore,
        });
      },
      {
        fireImmediately: true,
      }
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.selectedWellID,
      (wellId) => {
        if (this.wellListPanel.groupId === this.directionalDrillingEntity.groupId) {
          this.directionalDrillingEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      }
    );

    return () => {
      this.setInActiveTab(false);
      dispose();
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
    };
  };
}
